import * as React from 'react'
import type { CertificationTraining } from '@/lib/api/certifications'
import { useI18n } from '@/app/lib/i18n'
import { vars } from '@/ui/styles/theme.css'
import {
  estimateLessonMinutes,
  formatCourseDurationParts,
  useVideoDurationSecondsMap,
} from '@/app/lib/media/courseDuration'
import { CoursePageHero } from './CoursePageHero'
import { CourseLessonCard } from './CourseLessonCard'
import * as styles from './CoursePage.css'

export type CoursePageViewProps = {
  title: string
  heroImageUrl?: string | null
  trainings: CertificationTraining[]
  /** Module-level progress percent when enrolled (frozen at 100 when complete). */
  progressPercent?: number | null
  /** When false, action buttons are inert (admin hover preview). */
  interactive?: boolean
  /** Use content-sized shell instead of viewport height (admin preview). */
  preview?: boolean
  onBeginOrContinue?: () => void
  onWatchVideo?: (trainingId: string) => void
  onTakeQuiz?: (trainingId: string) => void
  onStartLesson?: (trainingId: string) => void
}

/** First training without a passed attempt, or the first training if none started. */
export function findContinueTraining(
  trainings: CertificationTraining[],
): CertificationTraining | null {
  const sorted = [...trainings].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber,
  )
  if (sorted.length === 0) return null
  return sorted.find((t) => !t.userProgress?.passed) ?? sorted[0] ?? null
}

export function findContinueTrainingId(trainings: CertificationTraining[]): string | null {
  return findContinueTraining(trainings)?.id ?? null
}

function CourseProgressRing({ percent }: { percent: number }) {
  const size = 64
  const stroke = 7
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const clamped = Math.max(0, Math.min(100, Math.round(percent)))
  const offset = circumference * (1 - clamped / 100)

  return (
    <svg
      className={styles.courseProgressRing}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${clamped}% complete`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={vars.color.border}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={vars.color.primary}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize="14"
        fontWeight="700"
        fill={vars.color.fg}
      >
        {clamped}%
      </text>
    </svg>
  )
}

export function CoursePageView({
  title,
  heroImageUrl,
  trainings,
  progressPercent,
  interactive = true,
  preview = false,
  onBeginOrContinue,
  onWatchVideo,
  onTakeQuiz,
  onStartLesson,
}: CoursePageViewProps) {
  const { t, tx } = useI18n()
  const sorted = React.useMemo(
    () => [...trainings].sort((a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber),
    [trainings],
  )

  const hasProgress = sorted.some((tr) => tr.userProgress?.passed)
  const continueTraining = findContinueTraining(sorted)
  const nextLessonTitle = continueTraining?.title ?? null

  const computedPercent = React.useMemo(() => {
    if (typeof progressPercent === 'number') return progressPercent
    if (sorted.length === 0) return 0
    const passed = sorted.filter((tr) => tr.userProgress?.passed).length
    return Math.round((passed / sorted.length) * 100)
  }, [progressPercent, sorted])

  // Client probe only for rows that still lack a server-stored duration (legacy / failed resolve).
  const urlsNeedingProbe = React.useMemo(
    () =>
      sorted
        .filter((tr) => tr.videoUrl && (tr.videoDurationSeconds == null || tr.videoDurationSeconds <= 0))
        .map((tr) => tr.videoUrl),
    [sorted],
  )
  const probedByUrl = useVideoDurationSecondsMap(urlsNeedingProbe)

  const lessonMinutes = React.useMemo(
    () =>
      sorted.map((training) => {
        const stored = training.videoDurationSeconds
        const probed = training.videoUrl ? probedByUrl[training.videoUrl] : null
        return estimateLessonMinutes({
          videoDurationSeconds:
            stored != null && stored > 0 ? stored : probed,
          questionCount: training.questionCount ?? training.questions?.length ?? 0,
        })
      }),
    [sorted, probedByUrl],
  )

  const totalMinutes = lessonMinutes.reduce((sum, m) => sum + m, 0)
  const remainingMinutes = sorted.reduce((sum, training, index) => {
    if (training.userProgress?.passed) return sum
    return sum + (lessonMinutes[index] ?? 0)
  }, 0)
  const durationParts = formatCourseDurationParts(totalMinutes)
  const remainingParts = formatCourseDurationParts(remainingMinutes)

  const courseTimeLabel =
    durationParts.unit === 'minutes'
      ? `${durationParts.value} ${t('certification.course.minutesUnit')}`
      : durationParts.remainderMinutes
        ? `${durationParts.value} ${t('certification.course.hoursUnit')} ${durationParts.remainderMinutes} ${t('certification.course.minutesUnit')}`
        : `${durationParts.value} ${t('certification.course.hoursUnit')}`

  const remainingTooltip =
    remainingMinutes <= 0
      ? t('certification.course.remainingNone')
      : remainingParts.unit === 'minutes'
        ? t('certification.course.remainingMinutes', { count: remainingParts.value })
        : remainingParts.remainderMinutes
          ? t('certification.course.remainingHoursMinutes', {
              hours: remainingParts.value,
              minutes: remainingParts.remainderMinutes,
            })
          : t('certification.course.remainingHours', { count: remainingParts.value })

  return (
    <div className={[styles.page, preview ? styles.pagePreview : ''].filter(Boolean).join(' ')}>
      <div className={[styles.scrollArea, preview ? styles.scrollAreaPreview : ''].filter(Boolean).join(' ')}>
        <CoursePageHero
          title={title}
          heroImageUrl={heroImageUrl}
          hasProgress={hasProgress}
          nextLessonTitle={nextLessonTitle}
          interactive={interactive}
          onBeginOrContinue={onBeginOrContinue}
        />

        <div className={styles.pageContent}>
          <div className={styles.courseHeader}>
            <h2 className={styles.courseSectionsTitle}>{tx('certification.course.sections')}</h2>
            <div className={styles.courseStats}>
              <div
                className={styles.courseStatTooltipWrap}
                title={remainingTooltip}
              >
                <span className={styles.courseStatTooltip} role="tooltip">
                  {remainingTooltip}
                </span>
                <CourseProgressRing percent={computedPercent} />
                <div className={styles.courseStat}>
                  <span className={styles.courseStatLabel}>{tx('certification.course.estTime')}</span>
                  <span className={styles.courseStatValue}>{courseTimeLabel}</span>
                </div>
              </div>
            </div>
          </div>

          {sorted.length === 0 && (
            <p style={{ color: '#6B7280' }}>{tx('certification.course.noTrainings')}</p>
          )}

          <div className={styles.lessonGrid}>
            {sorted.map((training, index) => (
              <CourseLessonCard
                key={training.id}
                title={training.title}
                section={training.section}
                videoUrl={training.videoUrl}
                thumbnailUrl={training.thumbnailUrl}
                stepNumber={training.stepNumber || index + 1}
                estimatedMinutes={lessonMinutes[index]}
                questionCount={training.questionCount ?? training.questions?.length}
                userProgress={training.userProgress}
                fallbackIndex={index}
                interactive={interactive}
                onWatchVideo={() => onWatchVideo?.(training.id)}
                onTakeQuiz={() => onTakeQuiz?.(training.id)}
                onStartLesson={() => onStartLesson?.(training.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
